import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { usePackageConfiguration } from '../../contexts';
import {
  getSpaPackageConfigGroups,
  getSpaPackageConfigGroupsKey,
} from '@features/spa/hooks/useGetSpaPackageConfigGroups';
import { PackagesFormValues } from '../../types';
import { getExperienceDates } from '../../helpers/getExperienceDates';
import moment from 'moment';

export const usePrefetchMultiGuestAvailability = () => {
  const queryClient = useQueryClient();
  const { property, spaPackageId, spaOpenTime, spaCloseTime, journeyParams, spaGuestAmount } =
    usePackageConfiguration();
  const { watch } = useFormContext<PackagesFormValues>();
  const selectedExperiences = watch('selectedExperiences');

  useEffect(() => {
    if (!selectedExperiences || selectedExperiences.length === 0) return;
    if (spaGuestAmount < 2) return;
    if (!spaPackageId || !spaOpenTime) return;

    const prefetchForExperiences = async () => {
      const datesToCheck = getExperienceDates(selectedExperiences, moment(journeyParams.fromDate));

      const prefetchPromises = datesToCheck.flatMap((date) => [
        queryClient.prefetchQuery({
          queryKey: getSpaPackageConfigGroupsKey(
            property.id,
            Number(spaPackageId),
            date,
            1,
            spaOpenTime,
            spaCloseTime
          ),
          queryFn: () =>
            getSpaPackageConfigGroups(
              property.id,
              Number(spaPackageId),
              date,
              1,
              spaOpenTime,
              spaCloseTime
            ),
          staleTime: 60000,
        }),
        queryClient.prefetchQuery({
          queryKey: getSpaPackageConfigGroupsKey(
            property.id,
            Number(spaPackageId),
            date,
            spaGuestAmount,
            spaOpenTime,
            spaCloseTime
          ),
          queryFn: () =>
            getSpaPackageConfigGroups(
              property.id,
              Number(spaPackageId),
              date,
              spaGuestAmount,
              spaOpenTime,
              spaCloseTime
            ),
          staleTime: 60000,
        }),
      ]);

      await Promise.all(prefetchPromises);
    };

    prefetchForExperiences();
  }, [
    selectedExperiences,
    spaGuestAmount,
    property.id,
    spaPackageId,
    journeyParams.fromDate,
    spaOpenTime,
    spaCloseTime,
    queryClient,
  ]);
};
